import React, { useState } from 'react';
import { useGameLounge } from '../context/GameLoungeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiPlus, FiMinus, FiShoppingCart, FiCheck } = FiIcons;

function KioskModal({ station, onClose }) {
  const { state, dispatch } = useGameLounge();
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Alle');

  // Finde den Raum für diese Station
  const findStationRoom = () => {
    for (const room of state.rooms) {
      const foundStation = room.stations.find(s => s.id === station.id);
      if (foundStation) {
        return room.name;
      }
    }
    return 'Unbekannt';
  };

  const roomName = findStationRoom();

  const categories = ['Alle', ...new Set(state.kioskItems.map(item => item.category))];

  const filteredItems = selectedCategory === 'Alle'
    ? state.kioskItems
    : state.kioskItems.filter(item => item.category === selectedCategory);

  const addToCart = (item) => {
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      ));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const removeFromCart = (itemId) => {
    const existingItem = cart.find(cartItem => cartItem.id === itemId);
    if (existingItem && existingItem.quantity > 1) {
      setCart(cart.map(cartItem =>
        cartItem.id === itemId
          ? { ...cartItem, quantity: cartItem.quantity - 1 }
          : cartItem
      ));
    } else {
      setCart(cart.filter(cartItem => cartItem.id !== itemId));
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const submitOrder = () => {
    if (cart.length > 0) {
      dispatch({
        type: 'ADD_ORDER',
        payload: {
          stationId: station.id,
          items: cart,
          total: getTotalPrice()
        }
      });
      alert(`Bestellung für ${station.name} erfolgreich aufgegeben!`);
      onClose();
    }
  };

  const getItemQuantity = (itemId) => {
    const cartItem = cart.find(item => item.id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gray-900 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Kiosk Bestellung</h2>
            <p className="text-gray-300">{roomName} - {station.name} - {station.customer}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Category Filter */}
          <div className="mb-6">
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Items Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {filteredItems.map(item => {
              const quantity = getItemQuantity(item.id);
              return (
                <div key={item.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="mb-3">
                    <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-600">{item.category}</p>
                    <p className="text-lg font-bold text-green-600 mt-1">
                      {item.price.toFixed(2)} {state.settings.currency}
                    </p>
                  </div>

                  {quantity === 0 ? (
                    <button
                      onClick={() => addToCart(item)}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiPlus} />
                      <span>Hinzufügen</span>
                    </button>
                  ) : (
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="bg-red-500 hover:bg-red-600 text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      >
                        <SafeIcon icon={FiMinus} />
                      </button>
                      <span className="text-lg font-semibold text-gray-900">{quantity}</span>
                      <button
                        onClick={() => addToCart(item)}
                        className="bg-green-500 hover:bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      >
                        <SafeIcon icon={FiPlus} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Cart Summary */}
          {cart.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <SafeIcon icon={FiShoppingCart} />
                <span>Warenkorb</span>
              </h3>
              <div className="space-y-2 mb-3">
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span>{item.quantity}x {item.name}</span>
                    <span className="font-medium">
                      {(item.price * item.quantity).toFixed(2)} {state.settings.currency}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-blue-300 pt-2 flex justify-between font-bold text-lg">
                <span>Gesamt:</span>
                <span className="text-green-600">
                  {getTotalPrice().toFixed(2)} {state.settings.currency}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 border-t border-gray-200 flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={submitOrder}
            disabled={cart.length === 0}
            className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiCheck} />
            <span>Bestellung aufgeben ({getTotalPrice().toFixed(2)} {state.settings.currency})</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default KioskModal;